// The call you are on: who, how long, mute and hang up — and everything the
// caller already told the AI, so you never ask twice. Notes typed here carry
// into the wrap-up when the call ends, and from there onto the ticket.

import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Bot, ExternalLink, Headphones, Mic, MicOff, Phone, PhoneOff, User, UserPlus } from 'lucide-react';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import voiceService, { type VoiceSession } from '@services/voice.service';
import { parseHandoffReason } from './utils/parseHandoffReason';
import { fieldLabel } from './utils/fieldLabel';
import { TurnText } from './components/TurnText';
import { TransferDialog } from './components/TransferDialog';
import { CallParticipants } from './components/CallParticipants';
import { callControls } from './callConference';
import { outgoingCallLabel } from './outboundCall';
import { callClock } from './callConsole';

type Turn = { role: 'customer' | 'ai' | 'agent'; text: string; english?: string; language?: string; name?: string };

const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';
/** How often the call's conversation is re-read while it is on. */
const SESSION_REFRESH_MS = 5_000;
const LABEL ='co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]';

export default function ActiveCallPage() {
  const navigate = useNavigate();
  const {
    activeCall,
    outgoingCall,
    callContext,
    isMuted,
    callDurationSeconds,
    callNotes,
    mute,
    unmute,
    hangup,
    setCallDuration,
    setCallNotes,
  } = useVoiceAgentStore();

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [startTime] = useState(() => Date.now() - callDurationSeconds * 1000);
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [dialog, setDialog] = useState<'transfer' | 'conference' | null>(null);
  const participants = useVoiceAgentStore((s) => s.participants);
  const isGuest = useVoiceAgentStore((s) => s.isGuest);
  const controls = callControls({ activeCall, callContext, participants, isGuest, outgoingCall });
  const answeredAt = outgoingCall?.answeredAt;
  const waiting = !!outgoingCall && !answeredAt;
  const addColleagueOnAnswer = useVoiceAgentStore((s) => s.addColleagueOnAnswer);

  // "Merge" on the ring: open "Add to call" as soon as the call is here.
  useEffect(() => {
    if (!addColleagueOnAnswer || !controls.canAddColleague) return;
    useVoiceAgentStore.getState().setAddColleagueOnAnswer(false);
    setDialog('conference');
  }, [addColleagueOnAnswer, controls.canAddColleague]);

  // Call clock.
  useEffect(() => {
    if (!activeCall || waiting) return;
    // A call this agent placed is timed from when the customer answered.
    const from = answeredAt ? Date.parse(answeredAt) : startTime;
    timerRef.current = setInterval(() => setCallDuration(Math.max(0, Math.floor((Date.now() - from) / 1000))), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeCall, waiting, answeredAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nothing to show without a call; the wrap-up opens over the console.
  useEffect(() => {
    if (!activeCall) navigate('/live-call');
  }, [activeCall, navigate]);

  // The whole AI conversation, the summary and the collected details.
  const sessionId = callContext?.sessionId;
  // Re-read while the call is on: English renderings of turns in another
  // language can land a moment after the agent picks up.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const load = () =>
      voiceService
        .getSession(sessionId)
        .then((s) => !cancelled && setSession(s))
        .catch(() => {
          // The offer's own context still shows.
        });
    void load();
    const refresh = setInterval(load, SESSION_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(refresh);
    };
  }, [sessionId]);

  if (!activeCall || !callContext) return null;

  const offered = callContext.aiSummary;
  const parsed = parseHandoffReason(callContext.handoffReason, offered?.keyIssue);
  const summaryText: string | undefined =
    session?.aiSummaryText ||
    (offered?.text && offered.text !== 'No summary available' ? offered.text : undefined);
  const reason = session?.handoffReason || session?.handoff?.reason || parsed.briefReason;
  const sessionTurns: Turn[] = (session?.transcriptHistory ?? [])
    .filter((t) => (t.speaker === 'customer' || t.speaker === 'ai' || t.speaker === 'agent') && t.content?.trim())
    .map((t) => ({
      role: t.speaker as Turn['role'],
      text: t.content,
      english: t.contentEnglish,
      language: t.language,
      name: t.speakerName,
    }));
  const turns: Turn[] = sessionTurns.length ? sessionTurns : parsed.conversationTurns;
  // Where the AI's part ends and the conversation with a person begins.
  const firstAgentTurn = turns.findIndex((t) => t.role === 'agent');
  const handedOverAt = firstAgentTurn === -1 ? turns.length : firstAgentTurn;
  const details = Object.entries(session?.collectedDetails ?? {}).filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim() !== '',
  );
  const name = details.find(([k]) => k === 'customer_name')?.[1];
  const ticketId = callContext.ticketId || session?.ticketId;

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* The call: who, how long, and its controls. */}
      <header
        className="flex flex-none flex-wrap items-center gap-5 px-[22px] py-5"
        style={{ borderRadius: 14, background: 'var(--co-ink)', boxShadow: 'var(--co-shadow-card)' }}
      >
        <div
          className="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-[12px]"
          style={{ background: 'var(--co-brass)', color: 'var(--co-ink)' }}
        >
          <Phone className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="co-mono truncate text-[22px] font-bold tracking-[-.018em]" style={{ color: 'var(--co-on-ink)' }}>
            {voiceService.formatPhoneNumber(callContext.customerPhone || '') || 'Unknown caller'}
          </p>
          <p className="co-mono mt-[6px] text-[10.5px] uppercase tracking-[.12em]" style={{ color: 'var(--co-brass)' }}>
            {name ? `${String(name)} · ` : ''}
            {waiting && outgoingCall ? outgoingCallLabel(outgoingCall) : outgoingCall ? 'On a call you placed' : 'Live call'}
            {isMuted ? ' · you are muted' : ''}
          </p>
        </div>
        <span className="co-mono flex-none text-[30px] font-semibold tabular-nums" style={{ color: 'var(--co-on-ink)' }}>
          {waiting ? '' : callClock(callDurationSeconds)}
        </span>
        <div className="flex flex-none items-center gap-[10px]">
          <button
            type="button"
            onClick={isMuted ? unmute : mute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            className="flex h-11 items-center gap-2 rounded-[10px] border px-4 text-[13px] font-semibold"
            style={{ borderColor: 'var(--co-brass)', color: 'var(--co-on-ink)' }}
          >
            {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {isMuted ? 'Unmute' : 'Mute'}
          </button>
          {/* Only where the call can be moved once it is connected. */}
          {controls.canTransfer && (
            <button
              type="button"
              onClick={() => setDialog('transfer')}
              className="flex h-11 items-center gap-2 rounded-[10px] border px-4 text-[13px] font-semibold"
              style={{ borderColor: 'var(--co-brass)', color: 'var(--co-on-ink)' }}
            >
              <ArrowRightLeft className="h-4 w-4" />
              Transfer
            </button>
          )}
          {controls.canAddColleague && (
            <button
              type="button"
              onClick={() => setDialog('conference')}
              title="Add a colleague: all three of you talk"
              className="flex h-11 items-center gap-2 rounded-[10px] border px-4 text-[13px] font-semibold"
              style={{ borderColor: 'var(--co-brass)', color: 'var(--co-on-ink)' }}
            >
              <UserPlus className="h-4 w-4" />
              Merge
            </button>
          )}
          <button
            type="button"
            onClick={() => hangup()}
            disabled={!controls.canEnd}
            title={controls.canEnd ? undefined : "A ringing call can't be cancelled. It stops within 30 seconds."}
            className="flex h-11 items-center gap-2 rounded-[10px] px-4 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: 'var(--co-burgundy)' }}
          >
            <PhoneOff className="h-4 w-4" />
            {controls.leaveLabel}
          </button>
          <PageHeaderMeta />
        </div>
        <div className="w-full empty:hidden">
          <CallParticipants />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-[14px] lg:flex-row">
        {/* What was said before you joined, or on a call you placed, what's been said. */}
        <section className={`${CARD_CLASS} flex min-h-0 flex-col lg:w-3/5`} style={CARD}>
          <h2 className={`${LABEL} flex-none border-b border-[var(--co-line)] px-5 py-3`}>
            Conversation · {turns.length} messages
          </h2>
          <div className="co-scrollbar-none min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {turns.length === 0 ? (
              <p className="py-10 text-center text-[12.5px] text-[var(--co-ink-3)]">
                {outgoingCall
                  ? waiting
                    ? 'The conversation will appear here once the customer answers.'
                    : 'Nothing has been written down yet.'
                  : 'No conversation before the transfer.'}
              </p>
            ) : (
              <ol className="space-y-3">
                {turns.map((turn, i) => {
                  const fromCaller = turn.role === 'customer';
                  const fromAgent = turn.role === 'agent';
                  return (
                    <Fragment key={i}>
                      {i === handedOverAt && i > 0 && !outgoingCall && <HandedOver />}
                      <li className={fromCaller ? 'flex' : 'flex flex-row-reverse'}>
                        <div className={`flex max-w-[80%] gap-2 ${fromCaller ? '' : 'flex-row-reverse'}`}>
                          <span
                            className="mt-[2px] flex h-6 w-6 flex-none items-center justify-center rounded-full"
                            style={
                              fromCaller
                                ? { background: 'var(--co-sunken)', color: 'var(--co-ink-3)' }
                                : fromAgent
                                  ? { background: 'var(--co-brass-wash)', color: 'var(--co-brass-ink)' }
                                  : { background: 'var(--co-machine-wash)', color: 'var(--co-machine-ink)' }
                            }
                            title={fromCaller ? 'Caller' : fromAgent ? turn.name || 'Agent' : 'AI'}
                          >
                            {fromCaller ? (
                              <User className="h-3 w-3" />
                            ) : fromAgent ? (
                              <Headphones className="h-3 w-3" />
                            ) : (
                              <Bot className="h-3 w-3" />
                            )}
                          </span>
                          <div>
                            {fromAgent && (
                              <p className="co-mono text-right text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                                {turn.name || 'Agent'}
                              </p>
                            )}
                            <div
                              className="rounded-[10px] px-3 py-2 text-[13px] leading-[1.45] text-[var(--co-ink)]"
                              style={{
                                background: fromCaller
                                  ? 'var(--co-sunken)'
                                  : fromAgent
                                    ? 'var(--co-brass-wash)'
                                    : 'var(--co-machine-wash)',
                              }}
                            >
                              <TurnText content={turn.text} language={turn.language} english={turn.english} />
                            </div>
                          </div>
                        </div>
                      </li>
                    </Fragment>
                  );
                })}
                {handedOverAt === turns.length && !outgoingCall && <HandedOver />}
              </ol>
            )}
          </div>
        </section>

        {/* What you need while you talk. */}
        <aside className="co-scrollbar-none flex min-h-0 flex-col gap-[14px] overflow-y-auto lg:w-2/5">
          <div className={`${CARD_CLASS} space-y-4 px-5 py-4`} style={CARD}>
            {outgoingCall ? (
              <Field label="Call">{ticketId ? 'You called the customer on this ticket.' : 'You dialled this number.'}</Field>
            ) : (
              <Field label="Wants a person because">{reason}</Field>
            )}
            {summaryText && <Field label="Summary">{summaryText}</Field>}
            {session?.ticketTypeName && <Field label="Request">{session.ticketTypeName}</Field>}
            {ticketId && (
              <a
                href={`/tickets/${ticketId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[6px] text-[12.5px] font-semibold text-[var(--co-brass-ink)] hover:underline"
              >
                <ExternalLink className="h-[13px] w-[13px]" />
                Open ticket
              </a>
            )}
          </div>

          {details.length > 0 && (
            <div className={`${CARD_CLASS} px-5 py-4`} style={CARD}>
              <p className={LABEL}>Already collected</p>
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
                {details.map(([key, value]) => (
                  <div key={key} className="min-w-0">
                    <dt className="truncate text-[11px] text-[var(--co-ink-3)]">{fieldLabel(key)}</dt>
                    <dd className="break-words text-[13px] font-medium text-[var(--co-ink)]">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* A dialled number has no ticket for notes to go on. */}
          {(!outgoingCall || ticketId) && (
          <label className={`${CARD_CLASS} block px-5 py-4`} style={CARD}>
            <span className={LABEL}>Your notes</span>
            <textarea
              value={callNotes}
              onChange={(e) => setCallNotes(e.target.value)}
              rows={5}
              placeholder="Jot down what you agree; it goes to the ticket when you wrap up."
              className="mt-2 w-full resize-y rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-2 text-[13px] text-[var(--co-ink)] focus:border-[var(--co-brass)] focus:outline-none"
            />
          </label>
          )}
        </aside>
      </div>
      {dialog && <TransferDialog mode={dialog} onClose={() => setDialog(null)} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className={LABEL}>{label}</p>
      <p className="mt-1 text-[13px] leading-[1.45] text-[var(--co-ink)]">{children}</p>
    </div>
  );
}

/** The point in the conversation where the AI handed the caller to a person. */
function HandedOver() {
  return (
    <li className="flex justify-center py-1">
      <span
        className="co-mono rounded-full px-3 py-1 text-[9.5px] uppercase tracking-[.12em]"
        style={{ background: 'var(--co-brass-wash)', color: 'var(--co-brass-ink)' }}
      >
        Transferred to you
      </span>
    </li>
  );
}
